/**
 * The brand × city hub's data (web plan W3.3): the backend's `brandCityPage`
 * for a route slug pair (@/seo/brandCityHubs `brandCityHubKey`), in the
 * route's locale. The SEO server sends the same document with the same
 * variables (server/routes.mjs), so the head it builds is the one this page
 * shows.
 *
 * Status like a detail page (@/utils/detailStatus, web plan W0.2): a slug no
 * brand/city pair can have, a NOT_FOUND answer (unknown brand, unknown city,
 * wrong country) or a null result is `not_found`; 429 / 5xx / network is
 * `error` (robots untouched).
 */
import { BRAND_CITY_PAGE } from '@/graphql/queries';
import { usePreloadedQuery } from '@/hooks/routePreload';
import { brandCityPageVariables, type BrandCityHubPage } from '@/seo/brandCityHubs';
import type { Locale } from '@/seo/locales';
import { detailStatus, type DetailStatus } from '@/utils/detailStatus';
import { queryErrorMessage } from './useAppData';

export interface BrandCityPageState {
  hub: BrandCityHubPage | null;
  status: DetailStatus;
  error: string | null;
  refetch: () => void;
}

export function useBrandCityPage(
  key: { brand: string; city: string } | null,
  locale: Locale,
): BrandCityPageState {
  // The route module's server copy answers first (web plan W4.5).
  const { data, loading, error, refetch } = usePreloadedQuery<{ brandCityPage?: BrandCityHubPage | null }>(
    BRAND_CITY_PAGE,
    {
      skip: !key,
      variables: key ? brandCityPageVariables(key, locale) : undefined,
      // Retry goes back through `loading` instead of leaving the error up.
      notifyOnNetworkStatusChange: true,
    },
  );
  const hub = data?.brandCityPage ?? null;
  return {
    hub,
    status: detailStatus({ item: hub, loading, error, validParam: Boolean(key) }),
    error: queryErrorMessage(error),
    refetch: () => {
      void refetch();
    },
  };
}
